/**
 * Dedicated Oroboros host paths (`oroboros.trade`).
 * Legacy `/oroboros/*` URLs still resolve and canonicalize here.
 */

import { isOroborosBrandingLocalhost } from './branding/isLocalhost';

export const ORO_HOME_PATH = '/';
export const ORO_APP_PATH = '/app';
export const ORO_DOCS_PATH = '/docs';
export const ORO_AUTH_CALLBACK_PATH = '/auth/callback';
export const ORO_BRANDING_PATH = '/branding';
export const ORO_DESKTOP_LOGIN_PATH = '/desktop-login';
export const ORO_LEGACY_PREFIX = '/oroboros';

export type OroborosView =
  | 'landing'
  | 'terminal'
  | 'docs'
  | 'branding'
  | 'auth-callback'
  | 'notfound';

function hostnameOf(raw: string): string {
  const s = String(raw || '').trim().toLowerCase();
  if (!s) return '';
  try {
    if (/^https?:\/\//i.test(s)) return new URL(s).hostname.toLowerCase();
  } catch {
    /* fall through */
  }
  return s.split(':')[0].replace(/^\[|\]$/g, '');
}

/** True on the split Oroboros SPA, oroboros.trade, and Oroboros Railway previews. */
export function isOroborosDedicatedHost(hostname?: string): boolean {
  try {
    if (typeof __OROBOROS_SPA__ !== 'undefined' && __OROBOROS_SPA__) return true;
  } catch {
    /* grail bundle has no flag */
  }
  const host = hostnameOf(
    hostname || (typeof window !== 'undefined' ? window.location.hostname : ''),
  );
  if (!host) return false;
  if (host === 'oroboros.trade' || host === 'www.oroboros.trade' || host.endsWith('.oroboros.trade')) {
    return true;
  }
  if (host.includes('oroboros') && (host.endsWith('.up.railway.app') || host.endsWith('.railway.app'))) {
    return true;
  }
  return false;
}

export function stripJesterPrefix(pathname: string): string {
  const path = String(pathname || '').toLowerCase();
  if (path === '/jester' || path.startsWith('/jester/')) {
    const stripped = path.slice('/jester'.length) || '/';
    return stripped.startsWith('/') ? stripped : `/${stripped}`;
  }
  return path;
}

function trimTrailingSlash(pathname: string): string {
  const path = String(pathname || '') || '/';
  if (path === '/') return '/';
  return path.replace(/\/+$/, '') || '/';
}

function rewriteLegacyPrefix(path: string, from: string, to: string): string | null {
  if (path === from) return to;
  if (path.startsWith(`${from}/`)) return `${to}${path.slice(from.length)}` || to;
  return null;
}

/** Map `/oroboros/...` bookmarks onto `/`, `/app`, `/docs`, `/auth/callback`. */
export function rewriteLegacyOroborosPath(pathname: string): string {
  const path = trimTrailingSlash(stripJesterPrefix(pathname));
  const terminal = rewriteLegacyPrefix(path, '/oroboros/terminal', ORO_APP_PATH);
  if (terminal) return terminal;
  const nestedApp = rewriteLegacyPrefix(path, '/oroboros/app', ORO_APP_PATH);
  if (nestedApp) return nestedApp;
  const docs = rewriteLegacyPrefix(path, '/oroboros/docs', ORO_DOCS_PATH);
  if (docs) return docs;
  const auth = rewriteLegacyPrefix(path, '/oroboros/auth/callback', ORO_AUTH_CALLBACK_PATH);
  if (auth) return auth;
  const branding = rewriteLegacyPrefix(path, '/oroboros/branding', ORO_BRANDING_PATH);
  if (branding) return branding;
  const desktop = rewriteLegacyPrefix(path, '/oroboros/desktop-login', ORO_DESKTOP_LOGIN_PATH);
  if (desktop) return desktop;
  if (path === '/oroboros') return ORO_HOME_PATH;
  return path.startsWith('/') ? path : `/${path}`;
}

export function isOroborosProductPath(pathname: string, hostname?: string): boolean {
  const path = trimTrailingSlash(stripJesterPrefix(pathname));
  if (path === '/oroboros' || path.startsWith('/oroboros/')) return true;
  if (!isOroborosDedicatedHost(hostname)) return false;
  if (path === '/' || path === '') return true;
  if (path === ORO_APP_PATH || path.startsWith(`${ORO_APP_PATH}/`)) return true;
  if (path === '/terminal' || path.startsWith('/terminal/')) return true;
  if (path === ORO_DOCS_PATH || path.startsWith(`${ORO_DOCS_PATH}/`)) return true;
  if (path === ORO_AUTH_CALLBACK_PATH || path.startsWith(`${ORO_AUTH_CALLBACK_PATH}/`)) return true;
  if (path === ORO_BRANDING_PATH || path.startsWith(`${ORO_BRANDING_PATH}/`)) return true;
  if (path === ORO_DESKTOP_LOGIN_PATH || path.startsWith(`${ORO_DESKTOP_LOGIN_PATH}/`)) return true;
  return false;
}

export function resolveOroborosView(pathname: string): OroborosView {
  const path = rewriteLegacyOroborosPath(pathname);
  if (path === '/' || path === '') return 'landing';
  if (path === ORO_APP_PATH || path.startsWith(`${ORO_APP_PATH}/`)) return 'terminal';
  if (path === '/terminal' || path.startsWith('/terminal/')) return 'terminal';
  if (path === ORO_DOCS_PATH || path.startsWith(`${ORO_DOCS_PATH}/`)) return 'docs';
  if (path === ORO_AUTH_CALLBACK_PATH || path.startsWith(`${ORO_AUTH_CALLBACK_PATH}/`)) {
    return 'auth-callback';
  }
  if (path === ORO_BRANDING_PATH || path.startsWith(`${ORO_BRANDING_PATH}/`)) {
    return isOroborosBrandingLocalhost() ? 'branding' : 'notfound';
  }
  if (path === '/oroboros' || path.startsWith('/oroboros/')) return 'notfound';
  return 'notfound';
}

export function canonicalOroborosHref(
  pathname: string,
  search = '',
  hash = '',
): string | null {
  const raw = stripJesterPrefix(pathname);
  const next = rewriteLegacyOroborosPath(pathname);
  const current = trimTrailingSlash(raw);
  if (next === current) return null;
  const view = resolveOroborosView(pathname);
  if (view === 'notfound' && !raw.startsWith('/oroboros')) return null;
  return `${next}${search || ''}${hash || ''}`;
}
